import classNames from 'classnames';
import { useInstantSearch, useSearchBox } from 'react-instantsearch';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { So5FixtureEvent } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import type { ExtendedUIState } from '@sorare/core/src/components/search/InstantSearch/types';
import {
  useGQLSearchContext,
  useClearAllFilters as useSearchClearAllFilters,
  useFiltersCount as useSearchFiltersCount,
  useToggleChip,
} from '@sorare/core/src/contexts/graphql-search';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useSo5Competitions } from '@sorare/core/src/hooks/useSo5Competitions';
import { useVirtualToggleManager } from '@sorare/core/src/hooks/useVirtualToggleManager';
import type { Filter } from '@sorare/core/src/lib/filters';
import {
  FILTERS,
  TOGGLE_FILTERS,
  VIRTUAL_TOGGLE_FILTERS,
} from '@sorare/core/src/lib/filters';
import { filters } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { useCurrentMarketplaceUserContext } from '#src/contexts/currentMarketplaceUser/index.ts';
import { useFormatActiveFixtureLabel } from '#src/hooks/search/useFormatActiveFixtureLabel.ts';
import {
  isListFilterWidget,
  isRangeFilterWidget,
  useFormatFilterWidgetValue,
} from '#src/hooks/search/useFormatFilterWidgetValue/index.tsx';
import { useRelevantRefinements } from '#src/hooks/search/useRelevantRefinements.ts';
import { useClearAllFilters } from '#src/search/ClearAllFilters/index.tsx';
import { useFiltersCount } from '#src/search/FiltersManager/useFiltersCount.ts';
import { GQL_SEARCH_FILTERS } from '#src/searchCommonPlayer/AdvancedPlayerSearch/filters.tsx';

import { FilterChip } from './FilterChip/index.tsx';
import { ListActiveFilter } from './ListActiveFilter/index.tsx';
import { RangeActiveFilter } from './RangeActiveFilter/index.tsx';
import { VirtualToggleActiveFilters } from './VirtualToggleActiveFilters/index.tsx';
import type { RefinementItem } from './types.ts';

type VirtualChipHandler = {
  /** `single` renders a lone FilterChip; `multi` delegates to ActiveFilter for chip-per-value. */
  kind: 'single' | 'multi';
  resolveLabel: (value: string) => React.ReactNode;
};

const buildStandardRefinements = (refinementType: string, values: string[]) =>
  refinementType === 'string'
    ? values.map(value => ({
        value,
        label: value,
        type: 'disjunctive' as const,
      }))
    : values
        .map((value, i) => ({
          value,
          label: value,
          operator: i === 0 ? '>=' : '<=',
          type: 'numeric' as const,
        }))
        .filter(e => e.value);

const Container = styled.div`
  display: flex;
  gap: var(--unit);
  flex-wrap: nowrap;
  overflow-x: auto;
  & > * {
    flex: none;
  }
  @media ${tabletAndAbove} {
    flex-wrap: wrap;
    & > * {
      flex: initial;
    }
    &.forceScroll {
      flex-wrap: nowrap;
      & > * {
        flex: none;
      }
    }
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ActiveFilter = ({ item }: { item: RefinementItem }) => {
  const { formatFilterWidgetValue } = useFormatFilterWidgetValue();

  if (
    [
      TOGGLE_FILTERS.creditEligibleFilter.attribute,
      TOGGLE_FILTERS.onSaleFilter.attribute,
      TOGGLE_FILTERS.bidToEarnFilter.attribute,
    ].includesValue(item.attribute)
  ) {
    const [label] = formatFilterWidgetValue(item);

    return (
      <FilterChip
        label={label}
        onClose={() => {
          item.refine(item.refinements?.[0]);
        }}
      />
    );
  }

  if (isListFilterWidget(item)) {
    return <ListActiveFilter item={item} />;
  }

  if (isRangeFilterWidget(item)) {
    return <RangeActiveFilter item={item} />;
  }

  return null;
};

export const ActiveFilters = ({ forceScroll }: { forceScroll?: boolean }) => {
  const { formatMessage } = useIntl();
  const filtersCount = useFiltersCount({ countSealed: false });
  const items = useRelevantRefinements();
  const {
    indexUiState: { exclusions = {} },
    setIndexUiState,
  } = useInstantSearch<ExtendedUIState>();
  const { query, clear } = useSearchBox();
  const clearAllFilters = useClearAllFilters();
  const setVirtualToggleFilters = useVirtualToggleManager();

  if (!filtersCount) {
    return null;
  }

  return (
    <Container className={classNames({ forceScroll })}>
      {!!query && (
        <FilterChip
          label={
            <FormattedMessage
              defaultMessage="Search: {query}"
              values={{ query }}
            />
          }
          onClose={() => {
            clear();
          }}
        />
      )}
      {items.map(item => (
        <ActiveFilter key={item.attribute} item={item as RefinementItem} />
      ))}
      {Object.entries(exclusions)
        .filter(([, value]) => value)
        .map(([key]) => {
          const filter = (Object.values(FILTERS) as Filter[]).find(
            f => f.alias === key
          );
          return (
            <FilterChip
              key={key}
              label={filter ? formatMessage(filter.title) : key}
              onClose={() => {
                setIndexUiState(uiState => ({
                  ...uiState,
                  exclusions: {
                    ...uiState.exclusions,
                    [key]: false,
                  },
                }));
              }}
            />
          );
        })}
      <VirtualToggleActiveFilters
        setVirtualToggleFilters={setVirtualToggleFilters}
      />
      <Button size="small" onClick={clearAllFilters} color="red">
        <FormattedMessage {...filters.clearAll} />
      </Button>
    </Container>
  );
};

export const BackendActiveFilters = ({
  forceScroll,
}: {
  forceScroll?: boolean;
}) => {
  const {
    query,
    activeFacets,
    facets,
    setSearchParams,
    getRefinementType,
    getRefinementApiAttribute,
  } = useGQLSearchContext()!;
  const { myWatchlists } = useCurrentMarketplaceUserContext() || {};
  const { so5Competitions } = useSo5Competitions();
  const { formatMessage } = useIntl();
  const formatActiveFixtureLabel = useFormatActiveFixtureLabel();
  const toggleChip = useToggleChip();

  const filtersCount = useSearchFiltersCount();

  const clear = () => {
    setSearchParams({ q: [] });
  };
  const clearAllFilters = useSearchClearAllFilters();
  const items = Object.entries(activeFacets).filter(
    ([k, v]) => v.length && getRefinementType(k) !== 'menu'
  );

  const virtualHandlers: Record<string, VirtualChipHandler> = {
    [GQL_SEARCH_FILTERS.leagueFilter]: {
      kind: 'multi',
      resolveLabel: value =>
        so5Competitions.find(c => c.slug === value)?.displayName ??
        facets[GQL_SEARCH_FILTERS.leagueFilter]?.values.find(
          e => e.value === value
        )?.label ??
        value,
    },
    [GQL_SEARCH_FILTERS.notInLineupFilter]: {
      kind: 'single',
      resolveLabel: () => formatMessage(filters.notInLineupFilterLabel),
    },
    [GQL_SEARCH_FILTERS.watchlistFilter]: {
      kind: 'single',
      resolveLabel: value =>
        myWatchlists?.find(w => idFromObject(w.id) === value)?.title ?? value,
    },
    [GQL_SEARCH_FILTERS.gameweekFilter]: {
      kind: 'single',
      resolveLabel: value =>
        formatActiveFixtureLabel(value, So5FixtureEvent.CLASSIC),
    },
    [GQL_SEARCH_FILTERS.duplicateFilter]: {
      kind: 'single',
      resolveLabel: () =>
        formatMessage(VIRTUAL_TOGGLE_FILTERS.duplicateFilter.label),
    },
    [GQL_SEARCH_FILTERS.dailyFilter]: {
      kind: 'single',
      resolveLabel: value =>
        formatActiveFixtureLabel(value, So5FixtureEvent.DAILY),
    },
  };

  if (!filtersCount) {
    return null;
  }

  return (
    <Container className={classNames({ forceScroll })}>
      {!!query && (
        <FilterChip
          label={
            <FormattedMessage
              defaultMessage="Search: {query}"
              values={{ query }}
            />
          }
          onClose={() => {
            clear();
          }}
        />
      )}
      {items.map(([urlAttribute, values]) => {
        const refinementType = getRefinementType(urlAttribute);
        const refine = ({ value }: { value: string }) => {
          toggleChip({ value, attribute: urlAttribute });
        };

        if (refinementType === 'virtual') {
          const virtual = virtualHandlers[urlAttribute];
          if (!virtual) return null;
          if (virtual.kind === 'single') {
            const value = values[0];
            return (
              <FilterChip
                key={urlAttribute}
                label={virtual.resolveLabel(value)}
                onClose={() => refine({ value })}
              />
            );
          }
          return (
            <ActiveFilter
              key={urlAttribute}
              item={
                {
                  attribute: getRefinementApiAttribute(urlAttribute),
                  refine,
                  refinements: values.map(value => ({
                    value,
                    label: virtual.resolveLabel(value),
                    type: 'disjunctive',
                  })),
                } as unknown as RefinementItem<string>
              }
            />
          );
        }

        return (
          <ActiveFilter
            key={urlAttribute}
            item={
              {
                attribute: getRefinementApiAttribute(urlAttribute),
                refine,
                refinements: buildStandardRefinements(refinementType, values),
              } as unknown as RefinementItem<string>
            }
          />
        );
      })}
      <Button size="small" onClick={clearAllFilters} color="red">
        <FormattedMessage {...filters.clearAll} />
      </Button>
    </Container>
  );
};
